// Port of CoreModels/Geo/ElevationAndTime.swift.

import type { RoutePoint } from './models';

export interface ElevationStatistics {
  ascent: number;
  descent: number;
  minElevation?: number;
  maxElevation?: number;
}

/** Default hysteresis (m): 3 m suits DEM-derived profiles; use ~5–10 m for raw GPS altitude. */
export const defaultElevationThreshold = 3;

/**
 * Cumulative ascent/descent with trend-following hysteresis: a change of direction counts only once the elevation has
 * moved at least `threshold` metres from the last confirmed extreme; a confirmed climb is then counted in full.
 */
export function computeElevationStatistics(
  elevations: Iterable<number | undefined>,
  threshold = defaultElevationThreshold,
): ElevationStatistics {
  const stats: ElevationStatistics = { ascent: 0, descent: 0 };
  const hysteresis = Number.isFinite(threshold) ? Math.max(0, threshold) : defaultElevationThreshold;
  let anchor: number | undefined;
  let trend = 0;
  for (const e of elevations) {
    if (e === undefined || !Number.isFinite(e)) continue;
    stats.minElevation = Math.min(stats.minElevation ?? e, e);
    stats.maxElevation = Math.max(stats.maxElevation ?? e, e);
    if (anchor === undefined) {
      anchor = e;
      continue;
    }
    const diff = e - anchor;
    if (trend > 0 && diff > 0) {
      stats.ascent += diff;
      anchor = e;
    } else if (trend < 0 && diff < 0) {
      stats.descent += -diff;
      anchor = e;
    } else if (diff > 0 && diff >= hysteresis) {
      stats.ascent += diff;
      anchor = e;
      trend = 1;
    } else if (diff < 0 && -diff >= hysteresis) {
      stats.descent += -diff;
      anchor = e;
      trend = -1;
    }
  }
  return stats;
}

export function elevationStatisticsOfPoints(points: readonly RoutePoint[], threshold = defaultElevationThreshold) {
  return computeElevationStatistics(
    points.map((p) => p.elevation),
    threshold,
  );
}

/**
 * Baseline hiking time (DIN 33466): horizontal = km / 4 km/h, vertical = ascent / 300 m/h + descent / 500 m/h,
 * total = max(h, v) + min(h, v) / 2. Excludes breaks. Returns seconds.
 */
export const HikingTime = {
  horizontalSpeedKmh: 4,
  ascentMetersPerHour: 300,
  descentMetersPerHour: 500,
  estimate(distanceMeters: number, ascent: number, descent: number): number {
    const h = Math.max(0, distanceMeters) / 1000 / HikingTime.horizontalSpeedKmh;
    const v = Math.max(0, ascent) / HikingTime.ascentMetersPerHour + Math.max(0, descent) / HikingTime.descentMetersPerHour;
    return (Math.max(h, v) + Math.min(h, v) / 2) * 3600;
  },
};
